import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { FieldInput } from "@/components/masters/FieldInput";
import type {
  MasterDefinition,
  MasterRecord,
  MasterRecordPayload,
  MasterSummary,
} from "@/lib/api/types";
import { masterCodeOf } from "@/lib/api/types";
import { coerceToBlob, defaultValueFor, type FormValue } from "@/lib/form";

import styles from "./masters.module.css";

export type SaveResult = { ok: true; code: string } | { ok: false; error: string; violations: string[] };

/** Rows of every master this form can point at, keyed by master code — the
 * option lists for parent pickers, membership pickers and ref fields. */
export type RelatedRows = Record<string, { code: string; name: string }[]>;

type Props = {
  sport: string;
  master: MasterSummary;
  related: RelatedRows;
  /** Present when editing; absent when creating. */
  record?: MasterRecord;
  /** Pre-selects the parent when creating inside an existing branch, so
   *  "Inside X" actually nests instead of silently creating at the root. */
  parentCode?: string;
  onSave: (payload: MasterRecordPayload) => Promise<SaveResult>;
};

/** Intrinsic columns each binding exposes beyond name (which every master has). */
const INTRINSICS: Record<string, ("country" | "city" | "timezone" | "dates" | "ordinal")[]> = {
  participant: ["country"],
  venue: ["country", "city", "timezone"],
  "competition-node": ["dates", "ordinal"],
};

function initialValues(master: MasterDefinition, record?: MasterRecord): Record<string, FormValue> {
  const values: Record<string, FormValue> = {};
  for (const field of master.fields ?? []) {
    const existing = record?.values?.[field.key];
    values[field.key] =
      existing === undefined || existing === null
        ? defaultValueFor(field)
        : typeof existing === "boolean"
          ? existing
          : String(existing);
  }
  return values;
}

export function RecordForm({ sport, master, related, record, parentCode, onSave }: Props) {
  const navigate = useNavigate();
  const config = master.definition;
  const intrinsics = INTRINSICS[master.bindingKind] ?? [];
  const parentRelation = (config.relations ?? []).find((r) => r.kind === "parent");
  const memberOfRelations = (config.relations ?? []).filter((r) => r.kind === "memberOf");

  const [name, setName] = useState(record?.name ?? "");
  const [code, setCode] = useState(record?.code ?? "");
  const [country, setCountry] = useState(record?.country ?? "");
  const [city, setCity] = useState(record?.city ?? "");
  const [timezone, setTimezone] = useState(record?.timezone ?? "");
  const [status, setStatus] = useState(record?.status ?? "");
  const [ordinal, setOrdinal] = useState(record?.ordinal != null ? String(record.ordinal) : "");
  const [startDate, setStartDate] = useState(record?.startDate ?? "");
  const [endDate, setEndDate] = useState(record?.endDate ?? "");
  const [parent, setParent] = useState(record?.parent ?? parentCode ?? "");
  // Which sports see this record. Undefined until touched, so an existing
  // record is left exactly as it is unless somebody changes it here.
  const [shared, setShared] = useState<boolean | null>(
    record ? (record.sports ?? []).length === 0 : null,
  );
  const canShare = master.bindingKind === "participant";

  const [memberships, setMemberships] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const m of record?.memberships ?? []) initial[m.master] = m.code;
    return initial;
  });
  const [values, setValues] = useState<Record<string, FormValue>>(() =>
    initialValues(config, record),
  );
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<SaveResult | null>(null);

  const configuredFields = config.fields ?? [];

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setResult(null);

    const payload: MasterRecordPayload = {
      name,
      values: coerceToBlob(configuredFields, values),
      ...(code ? { code } : {}),
      ...(intrinsics.includes("country") && country ? { country } : {}),
      ...(intrinsics.includes("city") && city ? { city } : {}),
      ...(intrinsics.includes("timezone") && timezone ? { timezone } : {}),
      ...(master.bindingKind === "competition-node"
        ? {
            status: status || null,
            ordinal: ordinal === "" ? null : Number(ordinal),
            startDate: startDate || null,
            endDate: endDate || null,
          }
        : {}),
      ...(parentRelation ? { parent: parent || null } : {}),
      ...(memberOfRelations.length > 0
        ? {
            memberships: memberOfRelations
              .filter((r) => memberships[r.master])
              .map((r) => ({ master: r.master, code: memberships[r.master] })),
          }
        : {}),
      // An empty list means every sport — that is the rule, not a quirk: a
      // record says which sports it is in, and one saying nothing is claiming
      // nothing. Omitted entirely when this kind of record cannot be shared.
      ...(canShare && shared !== null ? { sports: shared ? [] : [sport] } : {}),
    };

    try {
      const saved = await onSave(payload);
      setResult(saved);
      if (saved.ok) navigate(`/${sport}/masters/${master.code}`);
    } finally {
      // Release the button even if the action rejects at the transport layer.
      setSubmitting(false);
    }
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      {result && !result.ok && (
        <div className={styles.errorBox}>
          <strong>{result.error}</strong>
          {result.violations.length > 0 && (
            <ul>
              {result.violations.map((v) => (
                <li key={v}>{v}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Details</h2>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="record-name">
            Name<span className={styles.required}>*</span>
          </label>
          <input
            id="record-name"
            className={styles.input}
            value={name}
            required
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="record-code">
            Code
          </label>
          <input
            id="record-code"
            className={styles.input}
            value={code}
            disabled={record !== undefined}
            placeholder={config.codeRule ? "auto from name" : "required"}
            onChange={(e) => setCode(e.target.value)}
          />
          <span className={styles.help}>
            {record !== undefined
              ? "A record's code is its permanent public id and cannot be changed."
              : config.codeRule
                ? `Left blank, it is derived from ${config.codeRule.from.join(" + ")}.`
                : "This master has no code rule, so a code is required."}
          </span>
        </div>

        {intrinsics.includes("country") && (
          <TextField id="country" label="Country (ISO-3)" value={country} onChange={setCountry} />
        )}
        {intrinsics.includes("city") && (
          <TextField id="city" label="City" value={city} onChange={setCity} />
        )}
        {intrinsics.includes("timezone") && (
          <TextField
            id="timezone"
            label="Timezone"
            value={timezone}
            onChange={setTimezone}
            help="IANA zone name, e.g. Asia/Kolkata. Defaults to UTC."
          />
        )}
        {master.bindingKind === "competition-node" && (
          <>
            <TextField id="status" label="Status" value={status} onChange={setStatus} />
            <TextField
              id="ordinal"
              label="Order"
              value={ordinal}
              onChange={setOrdinal}
              type="number"
            />
            <TextField
              id="startDate"
              label="Start date"
              value={startDate}
              onChange={setStartDate}
              type="date"
            />
            <TextField
              id="endDate"
              label="End date"
              value={endDate}
              onChange={setEndDate}
              type="date"
            />
          </>
        )}
      </section>

      {(parentRelation || memberOfRelations.length > 0) && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Relations</h2>
          {parentRelation && (
            <PickerField
              id="parent"
              label={parentRelation.label ?? `Parent ${parentRelation.master}`}
              required={parentRelation.required ?? false}
              value={parent}
              options={related[parentRelation.master] ?? []}
              onChange={setParent}
            />
          )}
          {memberOfRelations.map((relation) => (
            <PickerField
              key={relation.master}
              id={`member-${relation.master}`}
              label={relation.label ?? `Member of ${relation.master}`}
              required={relation.required ?? false}
              value={memberships[relation.master] ?? ""}
              options={related[relation.master] ?? []}
              onChange={(next) =>
                setMemberships((prev) => ({ ...prev, [relation.master]: next }))
              }
            />
          ))}
        </section>
      )}

      {canShare && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Which sports see this</h2>
          <label className={styles.checkRow}>
            <input
              type="checkbox"
              checked={shared === true}
              onChange={(e) => setShared(e.target.checked)}
            />
            <span>
              Every sport — it belongs to no one of them
              <span className={styles.help}>
                {" "}
                A country, a venue, anyone who is not this sport&apos;s alone. Shared records are
                listed in the Commons and are edited there.
              </span>
            </span>
          </label>
        </section>
      )}

      {configuredFields.length > 0 && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{config.label} fields</h2>
          {configuredFields.map((field) => (
            <FieldInput
              key={field.key}
              field={field}
              value={values[field.key] ?? defaultValueFor(field)}
              // A ref to a master renders as a picker of that master's rows.
              options={related[masterCodeOf(field.target) ?? ""] ?? []}
              dependsOnValue={
                field.dependsOn ? String(values[field.dependsOn] ?? "") : undefined
              }
              onChange={(next) => setValues((prev) => ({ ...prev, [field.key]: next }))}
            />
          ))}
        </section>
      )}

      <div className={styles.actions}>
        <button className={styles.submit} type="submit" disabled={submitting}>
          {submitting ? "Saving…" : record ? "Save changes" : `Create ${config.label.toLowerCase()}`}
        </button>
        <a className={styles.cancel} href={`/${sport}/masters/${master.code}`}>
          Cancel
        </a>
      </div>
    </form>
  );
}

function TextField({
  id,
  label,
  value,
  onChange,
  type = "text",
  help,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  help?: string;
}) {
  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={`record-${id}`}>
        {label}
      </label>
      <input
        id={`record-${id}`}
        className={styles.input}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {help && <span className={styles.help}>{help}</span>}
    </div>
  );
}

function PickerField({
  id,
  label,
  required,
  value,
  options,
  onChange,
}: {
  id: string;
  label: string;
  required: boolean;
  value: string;
  options: { code: string; name: string }[];
  onChange: (value: string) => void;
}) {
  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={`record-${id}`}>
        {label}
        {required && <span className={styles.required}>*</span>}
      </label>
      <select
        id={`record-${id}`}
        className={styles.select}
        value={value}
        required={required}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">Select…</option>
        {options.map((option) => (
          <option key={option.code} value={option.code}>
            {option.name}
          </option>
        ))}
      </select>
      {options.length === 0 && <span className={styles.help}>No records to choose from yet.</span>}
    </div>
  );
}
